import React from "react";
import PropTypes from "prop-types";

/**
 * Trạng thái phiếu + trạng thái chi tiền — một nguồn cho bảng danh sách, filter
 * và trang chi tiết. Mỗi trạng thái có icon riêng (không chỉ dựa vào màu) và
 * luôn đi kèm nhãn chữ.
 */

// Tông màu pill — Geist: chữ màu + nền tint nhạt + viền nhạt.
export const TONE = {
  neutral: { iconCls: "tw-text-geist-fg-3",   cls: "tw-bg-geist-100 tw-text-geist-fg-2 tw-border-geist-border" },
  muted:   { iconCls: "tw-text-geist-fg-3",   cls: "tw-bg-geist-100 tw-text-geist-fg-3 tw-border-geist-border" },
  warning: { iconCls: "tw-text-[#d97706]",    cls: "tw-bg-[#fff8ee] tw-text-[#b46a00] tw-border-[#f7e2c0]" },
  success: { iconCls: "tw-text-[#0f9f8f]",    cls: "tw-bg-[#e9f9f6] tw-text-[#04766a] tw-border-[#bfeae4]" },
  error:   { iconCls: "tw-text-geist-error",  cls: "tw-bg-[#fdeceb] tw-text-[#c0362f] tw-border-[#f6cfcc]" },
  info:    { iconCls: "tw-text-[#2563eb]",    cls: "tw-bg-[#eef4ff] tw-text-[#1d4ed8] tw-border-[#cfe0ff]" },
};

// status của phiếu (Constants::ERP_PAYMENT_REQUEST_STATUS_*).
export const STATUS = {
  0: { name: "Draft",    icon: "bi-pencil-square",    ...TONE.neutral },
  1: { name: "Pending",  icon: "bi-hourglass-split",  ...TONE.warning },
  2: { name: "Approved", icon: "bi-check-circle-fill", ...TONE.success },
  3: { name: "Rejected", icon: "bi-x-circle-fill",    ...TONE.error },
  4: { name: "Canceled", icon: "bi-slash-circle",     ...TONE.muted },
};

export const statusOf = (id) => STATUS[id] || STATUS[0];

// bank_status (Constants::ERP_PAYMENT_REQUEST_BANK_*).
export const BANK_STATUS = {
  not_sent:          { name: "Not sent",      icon: "bi-dash-circle",           ...TONE.neutral },
  pending_approvals: { name: "Awaiting bank", icon: "bi-bank",                  ...TONE.warning },
  processing:        { name: "Processing",    icon: "bi-arrow-repeat",          ...TONE.info },
  paid:              { name: "Paid",          icon: "bi-check-circle-fill",     ...TONE.success },
  failed:            { name: "Failed",        icon: "bi-exclamation-triangle-fill", ...TONE.error },
};

// Payment Accountant (non-HSBC): processing | paid.
export const DISBURSEMENT = {
  processing: BANK_STATUS.processing,
  paid:       BANK_STATUS.paid,
};

export const StatusIcon = ({ tone }) => (
  <i className={`bi ${tone.icon} ${tone.iconCls} tw-text-[11px] tw-leading-none`} aria-hidden="true" />
);

const toneShape = PropTypes.shape({
  icon: PropTypes.string,
  iconCls: PropTypes.string,
  cls: PropTypes.string,
});

StatusIcon.propTypes = {
  tone: toneShape.isRequired,
};

export const Pill = ({ tone, children }) => (
  <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-xl tw-border tw-border-solid tw-whitespace-nowrap ${tone.cls}`}>
    {tone.icon && <StatusIcon tone={tone} />}
    {children}
  </span>
);

Pill.propTypes = {
  tone: toneShape.isRequired,
  children: PropTypes.node,
};

export const StatusPill = ({ status }) => {
  const st = statusOf(status);
  return <Pill tone={st}>{st.name}</Pill>;
};

StatusPill.propTypes = {
  status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

/** Options cho FilterDropdown — có icon trước nhãn. */
export const STATUS_OPTIONS = [0, 1, 2, 3, 4].map((id) => ({
  value: id, label: STATUS[id].name, icon: <StatusIcon tone={STATUS[id]} />,
}));
